import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AnimationDetailsDTO } from "../../dto/AnimationDetailsDTO";
import { AnimationDTO } from "../../dto/AnimationDTO";
import { RootState } from "../Store";

/**
 * Caches are keyed by the animation's `externalId`. A `null` entry means the
 * API answered 404 for that id: the animation is unavailable and must not be
 * fetched again. `undefined` (no entry) means it has not been looked up yet.
 */
export interface AnimationLibraryState {
  isInitialized: boolean;
  filter: string;
  pagedAnimations: AnimationDTO[];
  selectedAnimation: AnimationDTO | null;
  selectedAnimationDetails: AnimationDetailsDTO | null;
  cachedAnimationDetails: Record<string, AnimationDetailsDTO | null>;
  cachedAnimationRenders: Record<string, string | null>;
}

/** Placeholder row for pages of the library that have not been fetched yet. */
export const unloadedAnimation: AnimationDTO = {
  externalId: "unloaded",
  name: "unloaded",
  animationState: "unloaded",
  layer: 0,
};

export function isUnloadedAnimation(
  animation: Pick<AnimationDTO, "externalId"> | null | undefined
): boolean {
  return !animation || animation.externalId === unloadedAnimation.externalId;
}

const initialAnimationLibraryState: AnimationLibraryState = {
  isInitialized: false,
  filter: "",
  pagedAnimations: [],
  selectedAnimation: null,
  selectedAnimationDetails: null,
  cachedAnimationDetails: {},
  cachedAnimationRenders: {},
};

export const animationLibrarySlice = createSlice({
  name: "animationLibrary",
  initialState: initialAnimationLibraryState,
  reducers: {
    initializeAnimationLibrary: (
      state,
      action: PayloadAction<{
        totalCount: number;
        firstPage: AnimationDTO[];
      }>
    ) => {
      const { totalCount, firstPage } = action.payload;
      state.isInitialized = true;
      state.pagedAnimations = new Array(totalCount).fill(unloadedAnimation);

      for (let i = 0; i < firstPage.length; i++) {
        state.pagedAnimations[i] = firstPage[i];
      }
    },
    updateAnimationLibrary: (
      state,
      action: PayloadAction<{
        pageNumber: number;
        page: AnimationDTO[];
      }>
    ) => {
      const { pageNumber, page } = action.payload;
      state.pagedAnimations.splice(pageNumber * 10, 10, ...page);
    },
    setSelectedAnimation: (state, action: PayloadAction<AnimationDTO>) => {
      state.selectedAnimation = action.payload;
    },
    setAnimationLibraryFilter: (state, action: PayloadAction<string>) => {
      state.filter = action.payload;
    },
    setSelectedAnimationDetails: (
      state,
      action: PayloadAction<AnimationDetailsDTO>
    ) => {
      state.selectedAnimationDetails = action.payload;
    },
    /** Pass `animationDetails: null` to record that the id is unavailable (404). */
    setCachedAnimationDetails: (
      state,
      action: PayloadAction<{
        externalId: string;
        animationDetails: AnimationDetailsDTO | null;
      }>
    ) => {
      state.cachedAnimationDetails[action.payload.externalId] =
        action.payload.animationDetails;
    },
    /** Pass `downloadUri: null` to record that the id has no preview (404). */
    setCachedAnimationRender: (
      state,
      action: PayloadAction<{
        externalId: string;
        downloadUri: string | null;
      }>
    ) => {
      state.cachedAnimationRenders[action.payload.externalId] =
        action.payload.downloadUri;
    },
    resetAnimationLibrary: () => initialAnimationLibraryState,
  },
});

export const {
  initializeAnimationLibrary,
  updateAnimationLibrary,
  setAnimationLibraryFilter,
  setSelectedAnimation,
  setSelectedAnimationDetails,
  setCachedAnimationDetails,
  setCachedAnimationRender,
  resetAnimationLibrary,
} = animationLibrarySlice.actions;

export const selectPagedAnimations = (state: RootState) =>
  state.animationLibrary.pagedAnimations;

export const selectAnimationLibraryFilter = (state: RootState) =>
  state.animationLibrary.filter;

export const selectSelectedAnimation = (state: RootState) =>
  state.animationLibrary.selectedAnimation;

export const selectSelectedAnimationDetails = (state: RootState) =>
  state.animationLibrary.selectedAnimationDetails;

export const selectCachedAnimationDetails = (state: RootState) =>
  state.animationLibrary.cachedAnimationDetails;

/** `undefined` = not looked up, `null` = unavailable (404), otherwise the details. */
export const selectCachedAnimationDetailsById = (
  state: RootState,
  externalId: string
): AnimationDetailsDTO | null | undefined =>
  state.animationLibrary.cachedAnimationDetails[externalId];

/** `undefined` = not looked up, `null` = no preview (404), otherwise the uri. */
export const selectCachedAnimationRenderById = (
  state: RootState,
  externalId: string
): string | null | undefined =>
  state.animationLibrary.cachedAnimationRenders[externalId];

export default animationLibrarySlice.reducer;
